import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Paper, Stack, Typography, useTheme } from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { PieChart } from '@mui/x-charts/PieChart';
import type { CarbonAvoidedByStream } from '../../types/esg-dashboard.type';
import { ESG_BLUE_PALETTE } from '../../constants/esgChartColors';
import {
  carbonMaterialLabel,
  formatEfVersions,
  formatTco2e,
} from '../../utils/carbonAvoidance.util';
import ChartCard from './ChartCard';

function formatPiePercent(value: number, total: number): string {
  if (total <= 0 || value <= 0) return '';
  const pct = (value / total) * 100;
  if (pct < 0.05) return '<0.1%';
  if (pct < 0.1) return '0.1%';
  if (pct < 1) return `${pct.toFixed(1)}%`;
  if (pct < 10) return `${pct.toFixed(1)}%`;
  return `${pct.toFixed(0)}%`;
}

interface CarbonAvoidedByStreamChartProps {
  streams: CarbonAvoidedByStream[];
  title: string;
  chartType: 'bar' | 'line' | 'area' | 'pie';
  onMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
  hideMenu?: boolean;
  fillHeight?: boolean;
  horizontalBar?: boolean;
}

const CarbonAvoidedByStreamChart: React.FC<CarbonAvoidedByStreamChartProps> = ({
  streams,
  title,
  chartType,
  onMenuClick,
  hideMenu = false,
  fillHeight = false,
  horizontalBar = false,
}) => {
  const theme = useTheme();
  const totalTco2e = useMemo(
    () => streams.reduce((s, row) => s + row.total_avoided_tco2e, 0),
    [streams]
  );

  const chartData = useMemo(
    () =>
      streams.map((row) => ({
        material_label: carbonMaterialLabel(row.material_label),
        total_avoided_tco2e: row.total_avoided_tco2e,
      })),
    [streams]
  );

  const measureRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 480, h: 300 });

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 480;
      const cw = Math.max(280, Math.floor(w));
      setDims({ w: cw, h: Math.max(260, Math.round(cw * 0.58)) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pieSeries = useMemo(
    () => [
      {
        type: 'pie' as const,
        data: streams.map((row, i) => ({
          id: i,
          value: row.total_avoided_tco2e,
          label: carbonMaterialLabel(row.material_label),
          color: ESG_BLUE_PALETTE[i % ESG_BLUE_PALETTE.length],
        })),
        innerRadius: '46%',
        outerRadius: '74%',
        paddingAngle: 2,
        cornerRadius: 10,
        startAngle: 0,
        endAngle: 360,
        arcLabel: (item: { value: number }) => formatPiePercent(item.value, totalTco2e),
        arcLabelMinAngle: 0,
      },
    ],
    [streams, totalTco2e]
  );

  if (!streams.length) {
    return (
      <Paper
        sx={{
          p: 3,
          borderRadius: '16px',
          boxShadow: theme.shadows[2],
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No carbon avoidance by stream for this period.
        </Typography>
      </Paper>
    );
  }

  if (chartType !== 'pie') {
    return (
      <ChartCard
        title={title}
        data={chartData}
        type={chartType}
        dataKey="total_avoided_tco2e"
        nameKey="material_label"
        segmentColors={ESG_BLUE_PALETTE}
        onMenuClick={onMenuClick}
        hideMenu={hideMenu}
        fillPaper={fillHeight}
        barLayout={horizontalBar ? 'horizontal' : 'vertical'}
      />
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        bgcolor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          {title}
        </Typography>
        {!hideMenu && onMenuClick && (
          <IconButton size="small" onClick={onMenuClick} sx={{ color: theme.palette.text.secondary }}>
            <MoreVertIcon />
          </IconButton>
        )}
      </Box>

      <Box ref={measureRef} sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <PieChart
            series={pieSeries}
            width={dims.w}
            height={dims.h}
            margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
            slotProps={{
              legend: { hidden: true },
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
          maxHeight: 220,
          overflowY: 'auto',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          All streams ({streams.length})
        </Typography>
        <Stack spacing={1}>
          {streams.map((row, i) => {
            const color = ESG_BLUE_PALETTE[i % ESG_BLUE_PALETTE.length];
            const label = carbonMaterialLabel(row.material_label);
            const efNote = formatEfVersions(row.emission_factor_versions);
            return (
              <Stack key={`${row.material_label}-${i}`} spacing={0.25}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ pr: 1 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: 0.5,
                      bgcolor: color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={label}>
                    {label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {formatTco2e(row.total_avoided_tco2e)} tCO2e
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ flexShrink: 0, minWidth: 56, textAlign: 'right' }}
                  >
                    {formatPiePercent(row.total_avoided_tco2e, totalTco2e) || '—'}
                  </Typography>
                </Stack>
                {efNote && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3.25 }}>
                    {efNote}
                  </Typography>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </Paper>
  );
};

export default CarbonAvoidedByStreamChart;
